import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import IntlUtil from '~shared/utils/IntlUtil'
import cls from './password-strength-box.module.scss'
import { Grid } from '@/shared/ui/Grid/Grid'
import { Text } from '@/shared/ui/Text/Text'
import { VerifiedIcon } from '@/shared/ui/Icon'

const isMissingLabel = label => {
  return !label || /^\{\{.+\}\}$/.test(label)
}

const getIntlLabel = key => {
  const label = IntlUtil.label(key)
  return isMissingLabel(label) ? undefined : label
}

const capitalizeFirst = value => {
  if (!value) return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

const getMinLength = patterns => {
  for (const pattern of patterns) {
    const match = pattern.match(/\{(\d+),/)
    if (match) return Number(match[1])
  }
}

const detectPatternType = pattern => {
  if (/\{(\d+),/.test(pattern)) return 'length'
  if (/\[a-z\]/.test(pattern)) return 'lower'
  if (/\[A-Z\]/.test(pattern)) return 'upper'
  if (/\\d|\[0-9\]/.test(pattern)) return 'digit'
  if (/\[\^\\w\]|\[\^A-Za-z0-9\]/.test(pattern)) return 'special'

  return 'other'
}

const testPattern = (pattern, password) => {
  try {
    return new RegExp(pattern).test(password || '')
  } catch (e) {
    return false
  }
}

const buildRules = (password, patterns = []) => {
  const matchedByType = {
    upper: undefined,
    lower: undefined,
    digit: undefined,
    special: undefined,
  }
  const existsByType = {
    upper: false,
    lower: false,
    digit: false,
    special: false,
  }

  let numberOfMatched = 0

  patterns.forEach(pattern => {
    const type = detectPatternType(pattern)
    if (!Object.prototype.hasOwnProperty.call(existsByType, type)) return

    existsByType[type] = true
    const isMatched = testPattern(pattern, password)
    matchedByType[type] =
      matchedByType[type] === undefined ? isMatched : matchedByType[type] || isMatched
  })

  const language = (IntlUtil.locale || '').toLowerCase()
  const isRu = language.startsWith('ru')

  const lowerLabel =
    getIntlLabel('PASSWORD_PATTERN_LOWERCASE') || (isRu ? 'строчные буквы' : 'lowercase letters')
  const upperLabel =
    getIntlLabel('PASSWORD_PATTERN_UPPERCASE') || (isRu ? 'заглавные буквы' : 'uppercase letters')
  const digitLabel = getIntlLabel('PASSWORD_PATTERN_DIGIT') || (isRu ? 'Цифры' : 'Digits')
  const specialLabel =
    getIntlLabel('PASSWORD_PATTERN_SPECIAL_CHAR') ||
    getIntlLabel('PASSWORD_PATTERN_SPECIAL') ||
    (isRu ? 'Специальные символы (#$&)' : 'Special characters (#$&)')

  const lettersLabel = isRu
    ? `${capitalizeFirst(upperLabel)} и ${lowerLabel}`
    : `${capitalizeFirst(upperLabel)} and ${lowerLabel}`

  const rules = []

  if (existsByType.upper || existsByType.lower) {
    const done =
      (existsByType.upper ? matchedByType.upper : true) &&
      (existsByType.lower ? matchedByType.lower : true)

    if (done) numberOfMatched++

    rules.push({
      id: 'letters',
      label: lettersLabel,
      done,
    })
  }

  if (existsByType.special) {
    const done = !!matchedByType.special

    if (done) numberOfMatched++

    rules.push({
      id: 'special',
      label: specialLabel,
      done,
    })
  }

  if (existsByType.digit) {
    const done = !!matchedByType.digit

    if (done) numberOfMatched++

    rules.push({
      id: 'digit',
      label: capitalizeFirst(digitLabel),
      done,
    })
  }

  return { rules, doneCount: numberOfMatched }
}

export const PasswordStrengthBox = ({ className, password, patterns }) => {
  const language = (IntlUtil.locale || '').toLowerCase()
  const isRu = language.startsWith('ru')

  const allPatterns = useMemo(() => {
    const required = patterns?.required || []
    const optional = patterns?.optional?.patterns || []
    return [...required, ...optional]
  }, [patterns])

  const minLength = useMemo(() => getMinLength(allPatterns), [allPatterns])

  const title = useMemo(() => {
    if (minLength) {
      return isRu ? `Минимум ${minLength} символов` : `Minimum ${minLength} characters`
    }

    const label = getIntlLabel('PASSWORD_PATTERN_LENGTH')
    if (label) return capitalizeFirst(label)
    return ''
  }, [isRu, minLength])

  const { rules, doneCount } = useMemo(() => {
    return buildRules(password, allPatterns)
  }, [password, allPatterns])

  return (
    <Grid container direction="column" className={classNames(cls.root, className)}>
      {title && <Text className={cls.title}>{title}</Text>}

      {rules.length > 0 && (
        <Grid container alignItems="center" className={cls.progress}>
          {Array(rules.length)
            .fill()
            .map((_rule, index) => (
              <div
                key={index}
                className={classNames(cls.segment, {
                  [cls.segmentDone]: index + 1 <= doneCount,
                })}
              />
            ))}
        </Grid>
      )}

      {rules.length > 0 ? (
        <div className={cls.rules}>
          {rules.map(rule => (
            <div
              key={rule.id}
              className={classNames(cls.rule, {
                [cls.ruleDone]: rule.done,
              })}
            >
              <VerifiedIcon
                className={classNames(cls.ruleIcon, {
                  [cls.ruleIconDone]: rule.done,
                })}
              />
              <Text className={cls.ruleLabel}>{rule.label}</Text>
            </div>
          ))}
        </div>
      ) : null}
    </Grid>
  )
}

PasswordStrengthBox.propTypes = {
  className: PropTypes.string,
  password: PropTypes.string,
  patterns: PropTypes.shape({
    required: PropTypes.arrayOf(PropTypes.string),
    optional: PropTypes.shape({
      min: PropTypes.number,
      patterns: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
}
